import { useEffect, useState } from "react";
import { Save } from "lucide-react";
import { api } from "../api";
import { Button } from "../ui";
import { useToast } from "../ui/toast";
import { useT } from "../i18n";

interface LocalProject {
  id: string;
  name: string;
  owner: string;
  custom_instructions: string | null;
}

export default function InstructionsPanel({ projectId }: { projectId: string }) {
  const toast = useToast();
  const t = useT();
  const [value, setValue] = useState("");
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const p = await api.get<LocalProject>(`/projects/${projectId}`);
        if (cancelled) return;
        setValue(p.custom_instructions ?? "");
        setLoaded(true);
      } catch {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch<LocalProject>(`/projects/${projectId}`, {
        custom_instructions: value.trim() || null,
      });
      setDirty(false);
      toast.ok(t.shared.instruktionen.gespeichert);
    } catch (e) {
      toast.err(t.common.speichernFehlgeschlagen, (e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <h3 className="text-[11px] font-semibold uppercase tracking-wider text-text-2">
          {t.shared.instruktionen.titel}
        </h3>
        {dirty && (
          <Button
            variant="primary"
            size="sm"
            onClick={save}
            loading={busy}
            icon={<Save className="w-3 h-3" />}
          >
            {t.common.speichern}
          </Button>
        )}
      </div>
      <textarea
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
          setDirty(true);
        }}
        onBlur={() => dirty && save()}
        placeholder={
          loaded ? t.shared.instruktionen.platzhalter : t.shared.instruktionen.laedt
        }
        aria-label={t.shared.instruktionen.feldAria}
        title={t.shared.instruktionen.feldTitel}
        rows={5}
        className="w-full text-[13px] bg-surface-2 border border-border rounded-md px-2.5 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500/40 resize-y leading-snug"
      />
    </div>
  );
}
